import {
  isValid,
  parse,
  startOfDay,
  isAfter,
  isSameDay,
  isEqual,
  addYears,
  isBefore,
} from "date-fns";
import z, { ZodType } from "zod";

export const PASSWORD_ALLOWED_CHARS_REGEX =
  /^[A-Za-z0-9@$&!%*?^_+\-=\[\]{}()|\\:";'<>.,\/#~`]+$/;

export const DATE_FORMAT = "yyyy-MM-dd";
export const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

// z.number на текстовых полях требует управляемый input чтобы парсить значения вручную
// z.coerce.number парсит пустые строки в ноль вместо undefined поэтому используем preprocess
// дополнительно клиент хочет доп валидацю на наличие нуля в начале строки
export const preprocessNumber = (schema: ZodType<number | undefined>) =>
  z.preprocess((input) => {
    const value = String(input).trim();

    if (value === "" || value === undefined) return undefined;

    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      return NaN;
    }

    return Number(value);
  }, schema);

export const validateDate = (s: string) => isValid(parse(s, DATE_FORMAT, new Date()));

export const validateDatetime = (s: string) => isValid(parse(s, DATETIME_FORMAT, new Date()));

export const validateEmptyDate = (s: string) => {
  if (s.trim() === "") {
    return true;
  }
  return validateDate(s);
};

export const validateEmptyDatetime = (s: string) => {
  if (s.trim() === "") {
    return true;
  }
  return validateDatetime(s);
};

export const validatePastDate = (s: string) => {
  if (!s || s.trim() === "") return true;
  const parsedDate = startOfDay(parse(s, DATE_FORMAT, new Date()));
  const today = startOfDay(new Date());
  return isAfter(parsedDate, today) || isSameDay(parsedDate, today);
};

export const validatePastDatetime = (s: string) => {
  if (!s || s.trim() === "") return true;
  const parsedDatetime = parse(s, DATETIME_FORMAT, new Date());
  const now = new Date();
  return isAfter(parsedDatetime, now) || isEqual(parsedDatetime, now);
};

export const validateDateNotMoreThanYearInFuture = (s: string) => {
  if (!s || s.trim() === "") return true;
  const parsedDate = startOfDay(parse(s, DATE_FORMAT, new Date()));
  const oneYearFromNow = startOfDay(addYears(new Date(), 1));
  return isBefore(parsedDate, oneYearFromNow) || isSameDay(parsedDate, oneYearFromNow);
};

export const validateDatetimeNotMoreThanYearInFuture = (s: string) => {
  if (!s || s.trim() === "") return true;
  const parsedDatetime = parse(s, DATETIME_FORMAT, new Date());
  const oneYearFromNow = addYears(new Date(), 1);
  return isBefore(parsedDatetime, oneYearFromNow) || isEqual(parsedDatetime, oneYearFromNow);
};

// Валидация, что дата не в будущем (может быть в прошлом или сегодня)
export const validateDateNotInFuture = (s: string) => {
  if (!s || s.trim() === "") return true;
  const parsedDate = startOfDay(parse(s, DATE_FORMAT, new Date()));
  const today = startOfDay(new Date());
  // Возвращаем true если дата не строго после сегодня (т.е. в прошлом или сегодня)
  return !isAfter(parsedDate, today);
};
